import type { ChatAttachment, EnvironmentId } from "@t3tools/contracts";

import { resolvePrimaryEnvironmentHttpUrl } from "./environments/primary/target";
import { readEnvironmentConnection } from "./environments/runtime";

/**
 * Uploads a composer attachment's bytes to the environment over HTTP and returns
 * the persisted {@link ChatAttachment} reference (id-based). Attachments are no
 * longer inlined as base64 in the WebSocket `thread.turn.start` command — a large
 * inline frame starves the RPC heartbeat and gets the socket killed mid-send.
 * Sent over XMLHttpRequest: fetch() exposes no upload progress, and its
 * streamed-body alternative requires HTTP/2.
 */
export function isUploadAbortedError(error: unknown): boolean {
  return error instanceof Error && error.name === "UploadAbortedError";
}

function uploadAbortedError(name: string): Error {
  const error = new Error(`Upload of '${name}' was stopped.`);
  error.name = "UploadAbortedError";
  return error;
}

export async function uploadComposerAttachment(input: {
  readonly environmentId: EnvironmentId;
  readonly threadId: string;
  readonly projectId: string;
  readonly type: "image" | "file";
  readonly file: File;
  readonly signal?: AbortSignal;
  readonly onProgress?: (loadedBytes: number) => void;
}): Promise<ChatAttachment> {
  const connection = readEnvironmentConnection(input.environmentId);
  if (!connection) {
    throw new Error(
      `Failed to upload attachment '${input.file.name}': the environment is unavailable.`,
    );
  }

  const name = input.file.name || (input.type === "image" ? "image" : "attachment");
  const mimeType =
    input.file.type || (input.type === "image" ? "image/png" : "application/octet-stream");
  const searchParams = {
    threadId: input.threadId,
    projectId: input.projectId,
    type: input.type,
    name,
    mimeType,
  };

  const uploadUrl =
    connection.kind === "primary"
      ? resolvePrimaryEnvironmentHttpUrl("/attachments", searchParams)
      : (() => {
          const url = new URL("/attachments", connection.knownEnvironment.target.httpBaseUrl);
          url.search = new URLSearchParams(searchParams).toString();
          return url.toString();
        })();

  const onProgress = input.onProgress;
  return await new Promise<ChatAttachment>((resolve, reject) => {
    if (input.signal?.aborted) {
      reject(uploadAbortedError(name));
      return;
    }
    const xhr = new XMLHttpRequest();
    xhr.open("POST", uploadUrl);
    xhr.withCredentials = true;
    xhr.setRequestHeader("Content-Type", mimeType);
    input.signal?.addEventListener("abort", () => xhr.abort(), { once: true });
    if (onProgress) {
      xhr.upload.addEventListener("progress", (event) => {
        if (event.lengthComputable) {
          onProgress(event.loaded);
        }
      });
    }
    xhr.addEventListener("load", () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(JSON.parse(xhr.responseText) as ChatAttachment);
        } catch (cause) {
          reject(
            new Error(
              `Failed to upload attachment '${name}': ${cause instanceof Error ? cause.message : String(cause)}`,
              { cause },
            ),
          );
        }
        return;
      }
      const detail = xhr.responseText.trim();
      reject(
        new Error(
          `Failed to upload attachment '${name}'${detail ? `: ${detail}` : ` (${xhr.status})`}.`,
        ),
      );
    });
    xhr.addEventListener("error", () => {
      reject(new Error(`Failed to upload attachment '${name}': network error.`));
    });
    xhr.addEventListener("abort", () => {
      reject(uploadAbortedError(name));
    });
    xhr.send(input.file);
  });
}
